import React, { useEffect, useMemo } from 'react'
import { makeStyles } from '@mui/styles'
import Loader from '@/modules/base/components/loader/loader'
import { buildInvoicePreviewDocuments } from '@/modules/document/utils/invoice-preview-documents'
import useInvoiceArtifacts from './use-invoice-artifacts'
import InvoiceArtifactPreview from './invoice-artifact-preview'

const useStyles = makeStyles(() => ({
  root: {
    position: 'relative',
    height: '100%',
    width: '100%',
  },
  loaderContainer: {
    position: 'relative',
    height: '100%',
    width: '100%',
  },
}))

interface InvoiceArtifactPreviewForFileProps {
  /** omxweb File id of the receipt to preview (receipt.fileId, falling back to receipt.id). */
  fileId?: string | number | null
  /**
   * Reports whether this file resolved to the artifact switcher (true) or fell back to the
   * legacy preview (false). Hosts that draw their own preview toolbar use it to hide that
   * toolbar, because the artifact preview brings its own zoom/rotate/download controls.
   */
  onResolved?: (hasArtifacts: boolean) => void
  /**
   * Host controls that the LEGACY preview carries inside `children` and that would otherwise
   * disappear as soon as this file switches to the artifact path — the expense/revenue edit
   * preview's „Dokument entfernen" button (QA 2026-09-15: „ausgaben mit e-rechnung -> dokument kann
   * nicht entfernt werden"). Rendered ONLY on the artifact path; on the legacy path `children`
   * still draws them itself, so passing them here never doubles them. A host that deliberately
   * offers no such control — the expense/revenue detail drawer, which mounts its legacy item with
   * `disableActions` — passes nothing, and nothing is drawn.
   */
  actions?: React.ReactNode
  /** The legacy preview, rendered whenever this file has no child artifacts. */
  children: React.ReactNode
}

/**
 * FLAG:useDocuAi — makes the invoice-artifact switcher (original / XML / validation report / report /
 * attachments) available in hosts that only ever knew a single file: the expense/revenue detail
 * drawer and the expense/revenue edit page. Those hosts get their receipts from the expense /
 * revenue payload, which carries no DMS relations, so the artifacts are resolved here from
 * `GET /file/:id/meta` — that route already enriches `invoiceRelatedFiles` + `validationResult` for
 * useDocuAi tenants (and only for them), so nothing new is exposed.
 *
 * Only mounted from a flag-ON branch in each host, so it carries no flag check itself; flag-OFF the
 * hosts render their legacy preview verbatim and this file is never reached.
 *
 * While the lookup runs, the legacy preview is deliberately NOT rendered: mounting it and tearing
 * it down a moment later is the exact double-mount that made the review preview re-download an
 * already-visible PDF (see embedded-invoice-review-preview-continuity.test.tsx).
 */
const InvoiceArtifactPreviewForFile: React.FC<InvoiceArtifactPreviewForFileProps> = ({
  fileId,
  onResolved,
  actions,
  children,
}) => {
  const classes = useStyles()
  // Shared with the review preview: resolves the artifacts and keeps refreshing them for a bounded
  // window, because the DocuAI children are linked after the analysis is reported ready.
  const { resolved: source, isResolving } = useInvoiceArtifacts(fileId)

  const hasArtifacts = useMemo(() => {
    if (!source) return false
    return buildInvoicePreviewDocuments(source).displayDocuments.length > 1
  }, [source])

  useEffect(() => {
    if (!isResolving) onResolved?.(hasArtifacts)
  }, [hasArtifacts, isResolving])

  // Report the fallback once on unmount too, so a host that hid its toolbar for this file
  // gets it back when the file goes away.
  useEffect(() => () => onResolved?.(false), [])

  if (isResolving) {
    return (
      <div className={classes.loaderContainer}>
        <Loader visible={true} />
      </div>
    )
  }

  if (!hasArtifacts || !source) return <>{children}</>

  return (
    <div className={classes.root}>
      {/* This wrapper already refreshes the artifacts, so the preview must not fetch the same
          file a second time. */}
      <InvoiceArtifactPreview document={source} refreshArtifacts={false} actions={actions} />
    </div>
  )
}

export default InvoiceArtifactPreviewForFile
